import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createServiceRequest,
  listServiceOfferings,
  listServiceWorkQueues,
} from "@/lib/service/client";
import type { FulfillmentUnit } from "@/lib/fulfillment/fulfillmentApi";
import type { ServiceOffering, ServiceWorkQueue } from "@/lib/service/types";
import type { ServiceRequestAttachment } from "@/lib/service/requestAttachmentsClient";
import {
  canSubmitServiceRequestWithPhotos,
  ServiceRequestPhotoUpload,
} from "@/components/service/ServiceRequestPhotoUpload";
import {
  serviceRequestDescriptionPlaceholder,
  serviceRequestIntakeHelpText,
  serviceRequestPhotosHelpText,
} from "@/lib/service/serviceRequestIntakeCopy";
import { ghostBtnClass, inputClass, primaryBtnClass } from "@/components/service/serviceShared";

type Props = {
  unit: FulfillmentUnit;
  open: boolean;
  onClose: () => void;
  onSubmitted?: () => void;
};

function newRequestId(): string {
  return crypto.randomUUID();
}

export function FulfillmentServiceRequestModal({ unit, open, onClose, onSubmitted }: Props) {
  const [queues, setQueues] = useState<ServiceWorkQueue[]>([]);
  const [offerings, setOfferings] = useState<ServiceOffering[]>([]);
  const [requestId, setRequestId] = useState(() => newRequestId());
  const [queueId, setQueueId] = useState("");
  const [offeringId, setOfferingId] = useState("");
  const [description, setDescription] = useState("");
  const [timeframe, setTimeframe] = useState("");
  const [attachments, setAttachments] = useState<ServiceRequestAttachment[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const selectedQueue = useMemo(
    () => queues.find((q) => q.id === queueId) ?? null,
    [queues, queueId]
  );
  const photosRequired = selectedQueue?.photos_required === true;
  const canSubmit = canSubmitServiceRequestWithPhotos(photosRequired, attachments.length);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setSuccess(null);
    setRequestId(newRequestId());
    setAttachments([]);
    void (async () => {
      const [qRes, oRes] = await Promise.all([
        listServiceWorkQueues({ active_only: true }),
        listServiceOfferings({ active_only: true }),
      ]);
      if (qRes.ok) {
        setQueues(qRes.data.queues);
        if (qRes.data.queues[0]) setQueueId(qRes.data.queues[0].id);
      }
      if (oRes.ok) setOfferings(oRes.data.offerings);
    })();
  }, [open]);

  const submit = useCallback(async () => {
    if (!description.trim()) {
      setError("Describe the operational scope — what work is needed on site.");
      return;
    }
    if (!canSubmit) {
      setError("Add at least one site photo for this work queue before submitting.");
      return;
    }
    setBusy(true);
    setError(null);
    const res = await createServiceRequest({
      id: requestId,
      origin: "sales",
      mfg_unit_id: unit.id,
      queue_id: queueId || null,
      offering_id: offeringId || null,
      description: description.trim(),
      preferred_timeframe: timeframe.trim(),
      location_label: unit.display_label,
    });
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setSuccess("Request submitted for service approval.");
    onSubmitted?.();
  }, [
    canSubmit,
    description,
    timeframe,
    queueId,
    offeringId,
    unit,
    requestId,
    onSubmitted,
  ]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-2xl border border-gray-200 bg-white p-5 shadow-xl dark:border-zinc-700 dark:bg-zinc-900">
        <h3 className="text-lg font-bold text-gray-900 dark:text-zinc-100">Request repair / upgrade</h3>
        <p className="mt-1 text-sm text-gray-600 dark:text-zinc-400">
          Unit <strong>{unit.display_label}</strong> — operational intake for Service → Inbound
          requests. This is not a pricing calculator.
        </p>

        <div className="mt-4 space-y-3">
          <select className={inputClass} value={queueId} onChange={(e) => setQueueId(e.target.value)}>
            <option value="">Work queue (optional)</option>
            {queues.map((q) => (
              <option key={q.id} value={q.id}>
                {q.label}
                {q.photos_required ? " · photos required" : ""}
              </option>
            ))}
          </select>
          <select
            className={inputClass}
            value={offeringId}
            onChange={(e) => setOfferingId(e.target.value)}
          >
            <option value="">Offering (optional)</option>
            {offerings.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
          <textarea
            className={`${inputClass} min-h-[80px]`}
            placeholder={serviceRequestDescriptionPlaceholder(selectedQueue)}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <p className="text-xs text-gray-500 dark:text-zinc-400">
            {serviceRequestIntakeHelpText(selectedQueue)}
          </p>
          <input
            className={inputClass}
            placeholder="Preferred timeframe (optional)"
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value)}
          />
          <div>
            {serviceRequestPhotosHelpText(selectedQueue) ? (
              <p className="mb-1 text-xs text-amber-800 dark:text-amber-200">
                {serviceRequestPhotosHelpText(selectedQueue)}
              </p>
            ) : null}
            <ServiceRequestPhotoUpload
              requestId={requestId}
              required={photosRequired}
              attachments={attachments}
              onAttachmentsChange={setAttachments}
              disabled={busy}
            />
          </div>
        </div>

        {error ? (
          <p className="mt-3 text-sm text-red-700 dark:text-red-300">{error}</p>
        ) : null}
        {success ? (
          <p className="mt-3 text-sm text-emerald-700 dark:text-emerald-300">{success}</p>
        ) : null}

        <div className="mt-4 flex justify-end gap-2">
          <button type="button" className={ghostBtnClass} onClick={onClose} disabled={busy}>
            Close
          </button>
          <button
            type="button"
            className={primaryBtnClass}
            onClick={() => void submit()}
            disabled={busy || !canSubmit}
          >
            {busy ? "Submitting…" : "Submit request"}
          </button>
        </div>
      </div>
    </div>
  );
}
